import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import {
  FiChevronDown,
  FiChevronLeft,
  FiChevronRight,
  FiLock,
  FiPlus,
  FiSearch,
  FiShield,
  FiTrash2,
  FiUserCheck,
  FiUserPlus,
  FiUsers,
} from 'react-icons/fi';
import { useSearchParams } from 'react-router-dom';

import { getServiceId } from '@/api/tokens.helper';
import { imageVariantLink } from '@/common/media';
import { AddSlot, ConfirmationModal, StatusPill } from '@/components';
import { Avatar } from '@/components/Avatar/Avatar';
import { ButtonOutlinedErrorSmall } from '@/components/Button';
import ContextMenu from '@/components/ContextMenu/ContextMenu';
import { Header } from '@/components/Header';
import { Input } from '@/components/Inputs';
import { useIsMobile } from '@/hooks/useIsMobile';
import { CREW_INVITES_KEY } from '@/pages/Crew/crewRoster';
import { AddCrewModal } from '@/pages/MyYachts/components/CrewManagement/AddCrewModal';
import { FRONTEND_FEATURES } from '@/permissions/frontend-features';
import { grantLock, type GrantLock, grantsOnlyHeld } from '@/permissions/grantable';
import { buildPermissionGates, type PermissionGates } from '@/permissions/permission-gates';
import { PERMISSION_CATEGORIES, PERMISSIONS } from '@/permissions/permissions.catalog';
import { applyPermissionRules } from '@/permissions/permissions.rules';
import { useFrontendFeature } from '@/permissions/useFrontendFeature';
import { usePermissions as useViewerPermissions } from '@/permissions/usePermissions';
import { useActiveAccount } from '@/store/active-account/active-account.store';
import { useAuth } from '@/store/auth/auth.store';
import { useInvitesQuery } from '@/store/invites/invites.store';
import { usePermissions, usePlanSeatQuery } from '@/store/permissions/permissions.store';
import { assignRoleToPerson } from '@/store/persons/person.api';
import type { Person } from '@/store/persons/person.dto';
import {
  isOwnerGroupRole,
  normalizeRoles,
  sortBySeniority,
  SYSTEM_ROLES,
  UserRole,
} from '@/utils/roles';
import { toast } from '@/utils/toast';

import { ROLE_PARAM, TAB_PARAM } from '../SettingsPage/components/settings-anchors';
import { SUBSCRIPTIONS_HIDDEN } from '../SettingsPage/components/settings-flags';
import { EDIT_SUBSCRIPTION } from '../SubscriptionSettingsPage/subscription-access';

import { AddRoleModal } from './components/AddRoleModal/AddRoleModal';
import { AssignRoleModal } from './components/AssignRoleModal/AssignRoleModal';
import PermissionGroupCard from './components/PermissionGroupCard/PermissionGroupCard';
import PermissionProgress from './components/PersonDetailHeader/PermissionProgress';
import SaveStatusPill from './components/SaveStatusPill/SaveStatusPill';
import { useAutoSave } from './hooks/useAutoSave';
import {
  getPersonFullName,
  getPersonRightsForService,
  getPersonRoleForService,
  getPersonSeatTypeForService,
  getPersonServiceTypeForService,
} from './people.helpers';
import { PermissionCompareContext } from './PermissionCompareContext';
import { ROLE_COLORS } from './permissions.types';
import { hasPermissionMeta } from './permissions.ui';
import s from './PermissionSettingsPage.module.scss';

/** "1 person", "3 people". */
const count = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/**
 * Whether a role — and the people on it — is shown here. Guests and service providers never
 * are. The owner role and the co-owner template are, but only to the owner: the owner's own
 * to read (see `lockReason`), the co-owners' to edit — the backend refuses both to everyone
 * else, co-owners included.
 */
const isVisibleRole = (role: string, viewerIsOwner: boolean) =>
  !SYSTEM_ROLES.has(role) ||
  (viewerIsOwner && (role === UserRole.OWNER || role === UserRole.CO_OWNER));

const LOCKED_OWN = 'You can’t change your own permissions.';
const LOCKED_SAME_LEVEL = 'You can’t change permissions at the same role level.';
const LOCKED_HIGHER_LEVEL = 'You can’t change permissions of a role above yours.';
const LOCKED_NOT_HELD = 'You can only change permissions you have yourself.';
const LOCKED_OWNER_ONLY = 'Only the owner can change this permission.';
const LOCKED_OWNER_GROUP = 'Only the owner and co-owners can change this permission.';

const GRANT_LOCK_REASON: Record<GrantLock, string> = {
  'owner-only': LOCKED_OWNER_ONLY,
  'owner-group': LOCKED_OWNER_GROUP,
  'not-held': LOCKED_NOT_HELD,
};

/** The roles that run the vessel: below the owner group, nobody changes them or the people on them. */
const RUNS_THE_VESSEL = new Set<string>([UserRole.OWNER, UserRole.CO_OWNER, UserRole.CAPTAIN]);

const DEFAULT_ROLES = new Set<string>(Object.values(UserRole));

/** One of the platform's own roles, rather than one added on this vessel. */
const isDefaultRole = (role: string) => DEFAULT_ROLES.has(role);

export const PermissionSettingsPage = ({ embedded = false }: { embedded?: boolean } = {}) => {
  const isMobile = useIsMobile();
  const viewerIsOwner = useAuth((state) => !!state.session?.userRoles?.includes(UserRole.OWNER));
  const meId = useAuth((state) => state.session?.id ?? '');
  // The raw role string: `userRoles` drops anything outside `UserRole`, custom roles included.
  const myRole = useAuth(
    (state) => state.session?.services?.find((x) => x.service_id === getServiceId())?.role ?? '',
  );
  const { can, rights: myRights } = useViewerPermissions();
  // ponytail: the backend also wants the vessel to have no owner. Only that hands a captain
  // TransferVessel — the page cannot grant it — so the right stands in for the check.
  const actingCaptain = myRole === UserRole.CAPTAIN && can('TransferVessel');
  const {
    fetchServiceRolePermissions,
    upsertServiceRolePermissions,
    fetchBasePermissions,
    fetchSeatPermissions,
    fetchServicePeople,
    updatePersonPermissions,
    deleteServiceRolePermissions,
  } = usePermissions();
  const basePermissions = usePermissions((s) => s.basePermissions);
  const canPay = useFrontendFeature(FRONTEND_FEATURES.PaymentsUI);
  // Not listed and not counted: base permissions everyone holds anyway, and Payments while the
  // person's audience does not switch payments on.
  const basePermsSet = useMemo(() => {
    const hidden = new Set<string>(basePermissions);
    if (!canPay) hidden.add(PERMISSIONS.wallet.Payments);
    return hidden;
  }, [basePermissions, canPay]);
  const seatPermissions = usePermissions((s) => s.seatPermissions);
  // Read off the people service rather than the subscription: whoever edits roles here may
  // not hold GetSubscription, and a refused subscription would read as the free tier.
  const { data: plan } = usePlanSeatQuery(getServiceId());
  const planSeat = plan?.seat ?? null;
  // A lifetime plan an admin granted has no higher plan to buy: its locked rows say so instead.
  const planGranted = plan?.manuallyGranted ?? false;
  /**
   * The invite lands on the active *vessel* — that is the only service `AddCrewModal`
   * knows how to address. A service-provider account edits its own roles on this page, so
   * offering the slot there would send the invitation to whichever boat happened to be
   * selected. Left out rather than mis-aimed.
   */
  const canInviteToRole =
    useActiveAccount((state) => state.serviceType) === 'vessel' &&
    can(PERMISSIONS.users.InviteUser);

  const [activeSelectionType, setActiveSelectionType] = useState<'role' | 'person'>('role');

  const [permsByRole, setPermsByRole] = useState<Record<string, Set<string>>>({});
  const queryClient = useQueryClient();
  const [deleteRoleOpen, setDeleteRoleOpen] = useState(false);
  const [activeRoleName, setActiveRoleName] = useState('');
  const [addRoleOpen, setAddRoleOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);

  const [people, setPeople] = useState<Person[]>([]);
  const [permsByPerson, setPermsByPerson] = useState<Record<string, Set<string>>>({});
  const [activePersonId, setActivePersonId] = useState('');
  const [peopleLoaded, setPeopleLoaded] = useState(false);

  const [personRoleChanging, setPersonRoleChanging] = useState(false);
  const [roleMenuOpen, setRoleMenuOpen] = useState(false);
  const [roleMenuAnchor, setRoleMenuAnchor] = useState<HTMLButtonElement | null>(null);

  // Desktop drill-down (roles list → role detail), mirrors the settings prototype.
  const [rolesDrill, setRolesDrill] = useState(false);
  const [roleListQuery, setRoleListQuery] = useState('');
  const [permSearch, setPermSearch] = useState('');

  const [searchParams, setSearchParams] = useSearchParams();
  const roleParam = searchParams.get(ROLE_PARAM);

  useEffect(() => {
    const serviceId = getServiceId();
    fetchServiceRolePermissions(serviceId)
      .then((items) => {
        const filtered = items.filter((i) => isVisibleRole(i.role, viewerIsOwner));
        setPermsByRole(Object.fromEntries(filtered.map((i) => [i.role, new Set(i.permissions)])));
        setActiveRoleName(filtered[0]?.role ?? '');
      })
      .catch(() => {
        setPermsByRole({});
      });

    fetchBasePermissions().catch((err) => {
      console.error('base permissions error:', err);
    });

    // Until these land no row is gated, so the page reads as fully editable for a moment.
    fetchSeatPermissions().catch((err) => {
      console.error('seat permissions error:', err);
    });
  }, [fetchServiceRolePermissions, fetchBasePermissions, fetchSeatPermissions, viewerIsOwner]);

  useEffect(() => {
    if (peopleLoaded) return;
    const serviceId = getServiceId();
    fetchServicePeople(serviceId)
      .then((items) => {
        const filtered = items.filter((p) =>
          isVisibleRole(getPersonRoleForService(p, serviceId), viewerIsOwner),
        );
        setPeople(filtered);
        setPermsByPerson(
          Object.fromEntries(
            filtered.map((p) => [p.id, new Set(getPersonRightsForService(p, serviceId))]),
          ),
        );
        setActivePersonId(filtered[0]?.id ?? '');
        setPeopleLoaded(true);
      })
      .catch((err) => {
        console.error('service people error:', err);
        setPeopleLoaded(true);
      });
  }, [peopleLoaded, fetchServicePeople, viewerIsOwner]);

  useEffect(() => {
    if (!roleParam || !permsByRole[roleParam]) return;
    let cancelled = false;
    queueMicrotask(() => {
      if (cancelled) return;
      setActiveRoleName(roleParam);
      setActiveSelectionType('role');
      setPermSearch('');
      setRolesDrill(true);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete(ROLE_PARAM);
          return next;
        },
        { replace: true },
      );
    });
    return () => {
      cancelled = true;
    };
  }, [roleParam, permsByRole, setSearchParams]);

  const activeRolePermissions = permsByRole[activeRoleName];
  const activePerson = people.find((p) => p.id === activePersonId);
  const activePersonPermissions = permsByPerson[activePersonId];

  /**
   * Below the owner group nobody touches the roles that run the vessel or anyone on them —
   * permissions, role changes and invites alike — bar a captain running an ownerless vessel,
   * who reaches the other captains' permissions.
   */
  const levelLocked = !isOwnerGroupRole(myRole) && RUNS_THE_VESSEL.has(activeRoleName);
  // The acting captain invites a permanent captain, as the owner group does (people `checkInviteRole`).
  const inviteLocked = levelLocked && !(actingCaptain && activeRoleName === UserRole.CAPTAIN);
  /** Only the roles that run the vessel move people between roles (backend `checkRoleChange`). */
  const canChangeRoles = RUNS_THE_VESSEL.has(myRole) && !levelLocked;

  /**
   * Why the viewer can't change a role (no `personId`) or one person on it, or nothing if they
   * can. Mirrors the backend (`canManageRole`, `canManagePermissionsOf`): nobody changes their
   * own permissions, nor the owner role, nor — see `levelLocked` — anything at or above a
   * captain's. Below the captain your own role's template stays open: it only reaches the
   * people assigned to it later, never your own rights, which were copied at assignment.
   * The backend refuses all of these anyway; this keeps the page from offering a change it
   * would then fail to save.
   */
  const lockReason = (role: string, personId?: string) => {
    if (personId === meId) return LOCKED_OWN;
    if (!isOwnerGroupRole(myRole) && RUNS_THE_VESSEL.has(role) && !(personId && actingCaptain)) {
      return role === myRole ? LOCKED_SAME_LEVEL : LOCKED_HIGHER_LEVEL;
    }
    // Only the owner sees the owner role, so it is always their own.
    if (!personId && role === UserRole.OWNER) return LOCKED_OWN;
    return undefined;
  };
  const roleLockReason = lockReason(activeRoleName);
  const personLockReason =
    activePerson &&
    lockReason(getPersonRoleForService(activePerson, getServiceId()), activePersonId);

  /**
   * A permission past what the viewer may hand out (`grantLock`) is refused when added and kept
   * when removed, so its row cannot move.
   */
  const rowLockReason = (name: string) => {
    const lock = grantLock(name, { myRole, myRights, actingCaptain });
    return lock && GRANT_LOCK_REASON[lock];
  };

  const countsByPerson = useMemo(() => {
    const out: Record<string, number> = {};
    for (const [id, perms] of Object.entries(permsByPerson)) {
      let n = 0;
      for (const p of perms) if (!basePermsSet.has(p)) n++;
      out[id] = n;
    }
    return out;
  }, [permsByPerson, basePermsSet]);

  /**
   * A role is measured against the seat the vessel's plan buys — the same seat the server
   * picks when it decides which of a role's permissions it will let through. A person is
   * measured against the seat they personally sit in, which may be the cheaper crew one,
   * so the same permission can be plan-gated for a role and seat-gated for one of its people.
   */
  const roleGates = useMemo(
    () => buildPermissionGates(seatPermissions, planSeat, planSeat),
    [seatPermissions, planSeat],
  );

  const activePersonSeat = activePerson
    ? getPersonSeatTypeForService(activePerson, getServiceId())
    : null;

  const personGates = useMemo(
    () => buildPermissionGates(seatPermissions, planSeat, activePersonSeat),
    [seatPermissions, planSeat, activePersonSeat],
  );

  const roleSave = useAutoSave({
    key: activeRoleName || null,
    value: activeRolePermissions ?? null,
    save: (role, perms) =>
      upsertServiceRolePermissions(getServiceId(), {
        role,
        permissions: Array.from(perms),
      }),
    onError: () => toast.error('Couldn’t save the role permissions. Please try again.'),
  });

  const personSave = useAutoSave({
    key: activePersonId || null,
    value: activePersonPermissions ?? null,
    save: (id, perms) =>
      updatePersonPermissions({
        person_id: id,
        service_id: getServiceId(),
        permissions: Array.from(perms),
      }),
    onError: () => toast.error('Couldn’t save this person’s permissions. Please try again.'),
  });

  const handleRoleToggle = (rightName: string, enabled: boolean) => {
    if (roleLockReason || rowLockReason(rightName)) return;
    setPermsByRole((prev) => {
      const current = prev[activeRoleName];
      if (!current) return prev;
      const next = new Set(current);
      const isBlocked = (name: string) => !!roleGates.reasonFor(name) || !!rowLockReason(name);
      if (enabled) {
        next.add(rightName);
        applyPermissionRules(next, rightName, true, isBlocked);
      } else {
        next.delete(rightName);
        applyPermissionRules(next, rightName, false, isBlocked);
      }
      return { ...prev, [activeRoleName]: next };
    });
  };

  const handlePersonSelect = (id: string) => {
    setActivePersonId(id);
    setActiveSelectionType('person');
  };

  const handleAddRoleClick = () => setAddRoleOpen(true);

  const handleAddRoleConfirm = async (name: string) => {
    if (permsByRole[name]) {
      setActiveRoleName(name);
      setActiveSelectionType('role');
      setAddRoleOpen(false);
      return;
    }
    await upsertServiceRolePermissions(getServiceId(), {
      role: name,
      permissions: [],
    });
    setPermsByRole((prev) => ({ ...prev, [name]: new Set() }));
    setActiveRoleName(name);
    setActiveSelectionType('role');
    setAddRoleOpen(false);
  };

  const handlePersonToggle = (rightName: string, enabled: boolean) => {
    if (personLockReason || rowLockReason(rightName)) return;
    setPermsByPerson((prev) => {
      const current = prev[activePersonId];
      if (!current) return prev;
      const next = new Set(current);
      const isBlocked = (name: string) => !!personGates.reasonFor(name) || !!rowLockReason(name);
      if (enabled) {
        next.add(rightName);
        applyPermissionRules(next, rightName, true, isBlocked);
      } else {
        next.delete(rightName);
        applyPermissionRules(next, rightName, false, isBlocked);
      }
      return { ...prev, [activePersonId]: next };
    });
  };

  /**
   * Moves one person to one role, whoever they are.
   *
   * Two screens ask for this and they arrive from opposite directions: the person is open
   * and you pick a role for them, or the role is open and you pick someone to put in it.
   * Same call either way, so the person is an argument rather than whoever happens to be
   * selected.
   */
  const changePersonRole = async (person: Person, nextRole: string) => {
    if (!nextRole) return;
    const serviceId = getServiceId();
    if (nextRole === getPersonRoleForService(person, serviceId)) return;
    const serviceType =
      getPersonServiceTypeForService(person, serviceId) ||
      usePermissions.getState().serviceRolePermissions[0]?.service_type ||
      '';
    if (!serviceType) {
      toast.error('Couldn’t change the role. Please reload the page.');
      return;
    }
    setPersonRoleChanging(true);
    try {
      // The only unsaved permission edits that can exist are the selected person's, so
      // this is the one flush either direction needs.
      await personSave.flush();
      await assignRoleToPerson({
        person_id: person.id,
        service_id: serviceId,
        service_type: serviceType,
        role: nextRole,
      });
      const refreshed = await fetchServicePeople(serviceId);
      const filtered = refreshed.filter((p) =>
        isVisibleRole(getPersonRoleForService(p, serviceId), viewerIsOwner),
      );
      setPeople(filtered);
      setPermsByPerson(
        Object.fromEntries(
          filtered.map((p) => [p.id, new Set(getPersonRightsForService(p, serviceId))]),
        ),
      );
      // The person has moved out of this role's user list — fall back to the role view.
      setActiveSelectionType('role');
      toast.success('Role updated');
    } catch {
      toast.error('Couldn’t change the role. Please try again.');
    } finally {
      setPersonRoleChanging(false);
    }
  };

  const handlePersonRoleChange = async (nextRole: string) => {
    setRoleMenuOpen(false);
    if (!activePerson) return;
    await changePersonRole(activePerson, nextRole);
  };

  const handleAssignToRole = async (person: Person) => {
    setAssignOpen(false);
    await changePersonRole(person, activeRoleName);
  };

  const activePersonRole = activePerson
    ? getPersonRoleForService(activePerson, getServiceId())
    : '';
  const activePersonRolePermissions = activePersonRole ? permsByRole[activePersonRole] : undefined;

  /**
   * Both sides of the progress ring leave gated permissions out, so it reads as "of what
   * you can change here" rather than counting rows the server would refuse to move.
   */
  const totalRights = (gates: PermissionGates) =>
    PERMISSION_CATEGORIES.reduce(
      (sum, cat) =>
        sum +
        Object.values(PERMISSIONS[cat]).filter(
          (n) => !basePermsSet.has(n) && hasPermissionMeta(n) && !gates.reasonFor(n),
        ).length,
      0,
    );

  const countEnabled = (perms: ReadonlySet<string> | undefined, gates: PermissionGates) =>
    perms
      ? Array.from(perms).filter(
          (p) => !basePermsSet.has(p) && hasPermissionMeta(p) && !gates.reasonFor(p),
        ).length
      : 0;

  const nonBaseCount = (perms: ReadonlySet<string> | undefined) =>
    perms ? Array.from(perms).filter((p) => !basePermsSet.has(p)).length : 0;

  const peopleInRole = (roleName: string) =>
    people.filter((p) => getPersonRoleForService(p, getServiceId()) === roleName);

  // The viewer is never a candidate: nobody moves themselves to another role.
  const peopleOutsideRole = (roleName: string) =>
    people
      .filter((person) => person.id !== meId)
      .map((person) => ({ person, role: getPersonRoleForService(person, getServiceId()) }))
      .filter(({ role }) => role !== roleName);

  // Custom roles have no rank, so they follow the built-in ones in the order they came.
  const roleNames = sortBySeniority(Object.keys(permsByRole), (name) => normalizeRoles([name]));
  const visibleRoles = roleNames.filter((name) => {
    const q = roleListQuery.trim().toLowerCase();
    if (!q) return true;
    if (name.toLowerCase().includes(q)) return true;
    return peopleInRole(name).some((p) => getPersonFullName(p).toLowerCase().includes(q));
  });

  /**
   * Where a plan-gated row sends whoever pays. Only offered from inside Settings, which owns
   * the tab param, only where that deployment ships billing at all — with no subscriptions
   * section to reach, an upgrade button would lead nowhere — and only to a viewer who may
   * change the plan: anyone else would land on a plan they cannot buy.
   */
  const openSubscriptions =
    embedded && !SUBSCRIPTIONS_HIDDEN && myRights.has(EDIT_SUBSCRIPTION)
      ? () =>
          setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set(TAB_PARAM, 'subscriptions');
            next.delete(ROLE_PARAM);
            return next;
          })
      : undefined;

  const openRole = (name: string) => {
    setActiveRoleName(name);
    setActiveSelectionType('role');
    setPermSearch('');
    setRolesDrill(true);
  };

  const backToRoles = () => {
    setRolesDrill(false);
    setActiveSelectionType('role');
  };

  /**
   * Only a role somebody added can go: the platform's own roles are what everything else falls
   * back to. Deleting is for whoever runs the vessel and may change the role at all.
   */
  const canDeleteRole =
    !isDefaultRole(activeRoleName) && RUNS_THE_VESSEL.has(myRole) && !roleLockReason;

  // Fetched only while the confirmation is open: it decides whether the popup warns about them.
  const { data: vesselInvites } = useInvitesQuery({
    service_id: deleteRoleOpen ? getServiceId() : undefined,
  });
  const pendingForRole =
    vesselInvites?.items.filter((invite) => invite.role === activeRoleName).length ?? 0;

  const handleDeleteRole = async () => {
    const role = activeRoleName;
    try {
      await deleteServiceRolePermissions(getServiceId(), { role });
      // Its pending invitations now read Steward.
      void queryClient.invalidateQueries({ queryKey: ['invites-list'] });
      void queryClient.invalidateQueries({ queryKey: [CREW_INVITES_KEY] });
      // Every role picker reads this list; the role must not be offered any more.
      void queryClient.invalidateQueries({ queryKey: ['service-roles'] });
      setPermsByRole((prev) => {
        const next = { ...prev };
        delete next[role];
        return next;
      });
      setActiveRoleName(Object.keys(permsByRole).find((name) => name !== role) ?? '');
      backToRoles();
      toast.success('Role deleted');
    } catch (err: unknown) {
      console.error(err);
      toast.error("Couldn't delete the role");
    }
  };

  const renderRolesList = () => (
    <>
      <header className={s.detailHeader}>
        <div className={s.detailIcon}>
          <FiShield />
        </div>
        <div>
          <h2 className={s.detailTitle}>Roles &amp; Permissions</h2>
          <p className={s.detailSub}>Manage roles and what each can access.</p>
        </div>
      </header>

      <div className={s.roleToolbar}>
        <Input
          placeholder="Search roles or people"
          value={roleListQuery}
          onChange={(e) => setRoleListQuery(e.target.value)}
          startIcon={<FiSearch />}
          className={s.toolbarSearch}
        />
        <button type="button" className={s.addRoleBtn} onClick={handleAddRoleClick}>
          <FiPlus />
          Add Role
        </button>
      </div>

      <p className={s.groupLabel}>Roles · {visibleRoles.length}</p>
      <div className={s.group}>
        {visibleRoles.length === 0 && (
          <div className={s.empty}>No roles match. Please try widening your search.</div>
        )}
        {visibleRoles.map((name) => (
          <button
            key={name}
            type="button"
            className={`${s.row} ${s.roleRow}`}
            onClick={() => openRole(name)}
          >
            <span className={s.roleDot} style={{ background: ROLE_COLORS[name] ?? '#818cf8' }} />
            <div className={s.rowText}>
              <div className={s.rowTitle}>{name}</div>
              <div className={s.rowSub}>
                {count(countEnabled(permsByRole[name], roleGates), 'permission', 'permissions')} ·{' '}
                {count(peopleInRole(name).length, 'person', 'people')}
              </div>
            </div>
            <FiChevronRight className={s.navChevron} />
          </button>
        ))}
      </div>
    </>
  );

  const roleLabel = (name: string) =>
    name ? name.charAt(0).toUpperCase() + name.slice(1).replace(/-/g, ' ') : '';

  const renderRoleDetail = () => {
    const usersInRole = peopleInRole(activeRoleName);
    const isPerson = activeSelectionType === 'person';
    const targetPerms = isPerson ? activePersonPermissions : activeRolePermissions;
    const targetToggle = isPerson ? handlePersonToggle : handleRoleToggle;
    const targetStatus = isPerson ? personSave.status : roleSave.status;
    const targetGates = isPerson ? personGates : roleGates;
    const compare = isPerson ? activePersonRolePermissions : undefined;
    const lockedReason = isPerson ? personLockReason : roleLockReason;
    // The owner is named only by an ownership transfer, never by an assignment or an invite.
    const isOwnerRole = activeRoleName === UserRole.OWNER;
    const color = ROLE_COLORS[activeRoleName] ?? '#818cf8';

    const roleMenuItems = roleNames
      .filter((name) => name !== activeRoleName)
      .map((name) => ({
        label: name.charAt(0).toUpperCase() + name.slice(1),
        onClick: () => void handlePersonRoleChange(name),
      }));

    return (
      <>
        {!isMobile && (
          <button type="button" className={s.backBtn} onClick={backToRoles}>
            <FiChevronLeft /> Roles
          </button>
        )}

        <div className={s.roleDetailHead}>
          <span className={s.roleDot} style={{ background: color }} />
          <span className={s.roleDetailName}>{activeRoleName}</span>
          <div className={s.roleDetailStatus}>
            {targetStatus !== 'idle' ? (
              <SaveStatusPill status={targetStatus} />
            ) : (
              <PermissionProgress
                enabledCount={countEnabled(targetPerms, targetGates)}
                totalRights={totalRights(targetGates)}
              />
            )}
          </div>
        </div>
        {!isPerson && canDeleteRole && (
          <div className={s.roleDetailActions}>
            <ButtonOutlinedErrorSmall type="button" onClick={() => setDeleteRoleOpen(true)}>
              <FiTrash2 aria-hidden="true" /> Delete role
            </ButtonOutlinedErrorSmall>
          </div>
        )}
        {/* Always on screen, not only on hover: touch has no hover to show a title on. */}
        {lockedReason && (
          <StatusPill
            tone="warning"
            icon={<FiLock aria-hidden="true" />}
            label={lockedReason}
            wrap
            className={s.lockNote}
          />
        )}
        {/* Same reason as above: the per-row hover text never reaches a touch screen. */}
        {!lockedReason && grantsOnlyHeld(myRole) && (
          <StatusPill
            tone="info"
            icon={<FiLock aria-hidden="true" />}
            label={LOCKED_NOT_HELD}
            wrap
            className={s.lockNote}
          />
        )}
        <p className={s.groupLabel}>Users</p>
        <div className={s.usersRow}>
          <button
            type="button"
            className={`${s.userCard} ${!isPerson ? s.userCardActive : ''}`}
            onClick={() => setActiveSelectionType('role')}
          >
            <span className={s.allUsersAvatar}>
              <FiUsers />
            </span>
            <div className={s.userMeta}>
              <span className={s.userName}>{activeRoleName} Role</span>
              <span className={s.userSub}>
                <span className={s.userCount}>
                  {nonBaseCount(activeRolePermissions)} permissions
                </span>
              </span>
            </div>
          </button>
          {usersInRole.map((p) => {
            const personActive = isPerson && activePersonId === p.id;
            const personRole = getPersonRoleForService(p, getServiceId());
            const firstName = p.person_info?.first_name ?? '';
            const lastName = p.person_info?.last_name ?? '';
            return (
              <button
                key={p.id}
                type="button"
                className={`${s.userCard} ${personActive ? s.userCardActive : ''}`}
                onClick={() => handlePersonSelect(p.id)}
              >
                <Avatar
                  src={imageVariantLink(p.avatar, 36)}
                  firstName={firstName}
                  lastName={lastName}
                  size={36}
                />
                <div className={s.userMeta}>
                  <span className={s.userName}>{getPersonFullName(p)}</span>
                  <span className={s.userSub}>
                    {personRole && (
                      <span
                        className={s.userRole}
                        style={{ color: ROLE_COLORS[personRole] ?? '#818cf8' }}
                      >
                        {personRole}
                      </span>
                    )}
                    <span className={s.userCount}>{countsByPerson[p.id] ?? 0} permissions</span>
                  </span>
                </div>
              </button>
            );
          })}
          {!isOwnerRole && canChangeRoles && (
            <AddSlot
              size="sm"
              icon={<FiUserCheck size={18} />}
              label="Assign Existing Crew"
              hint={`Moves them to ${roleLabel(activeRoleName)}.`}
              className={s.inviteSlot}
              disabled={personRoleChanging}
              onClick={() => setAssignOpen(true)}
            />
          )}
          {canInviteToRole && !isOwnerRole && !inviteLocked && (
            <AddSlot
              size="sm"
              icon={<FiUserPlus size={18} />}
              label="Invite by Email"
              hint={`Joins as ${roleLabel(activeRoleName)}.`}
              className={s.inviteSlot}
              onClick={() => setInviteOpen(true)}
            />
          )}
        </div>

        {isPerson && activePerson && activePerson.id !== meId && !isOwnerRole && canChangeRoles && (
          <div className={s.rolePicker}>
            <button
              ref={setRoleMenuAnchor}
              type="button"
              className={s.rolePickerBtn}
              onClick={() => !personRoleChanging && setRoleMenuOpen((v) => !v)}
              disabled={personRoleChanging || roleMenuItems.length === 0}
              aria-haspopup="listbox"
              aria-expanded={roleMenuOpen}
            >
              {personRoleChanging ? 'Changing Role…' : 'Change Role'}
              <FiChevronDown />
            </button>
            <ContextMenu
              items={roleMenuItems}
              isOpen={roleMenuOpen && !personRoleChanging}
              onClose={() => setRoleMenuOpen(false)}
              anchorEl={roleMenuAnchor}
            />
          </div>
        )}

        <Input
          placeholder="Expenses"
          value={permSearch}
          onChange={(e) => setPermSearch(e.target.value)}
          startIcon={<FiSearch />}
          className={s.permSearch}
        />

        {targetPerms ? (
          <PermissionCompareContext.Provider value={compare}>
            <div className={s.permGrid}>
              {PERMISSION_CATEGORIES.map((category) => (
                <PermissionGroupCard
                  key={category}
                  category={category}
                  permissions={targetPerms}
                  excluded={basePermsSet}
                  onToggle={targetToggle}
                  search={permSearch}
                  gates={targetGates}
                  onUpgrade={openSubscriptions}
                  planGranted={planGranted}
                  lockedReasonFor={(name) => lockedReason || rowLockReason(name)}
                />
              ))}
            </div>
          </PermissionCompareContext.Provider>
        ) : null}
      </>
    );
  };

  return (
    <div className={s.pageDesktop}>
      {!embedded && <Header title="Roles & Permissions" menuButton={isMobile} backButton />}
      <div className={s.desktopPanel}>
        {rolesDrill && activeRolePermissions ? renderRoleDetail() : renderRolesList()}
      </div>
      <ConfirmationModal
        open={deleteRoleOpen}
        onClose={() => setDeleteRoleOpen(false)}
        onConfirm={() => void handleDeleteRole()}
        title={`Delete the ${activeRoleName} role?`}
        message={[
          "It can't be given to anyone any more.",
          pendingForRole > 0 &&
            `${pendingForRole === 1 ? 'The pending invitation' : `The ${pendingForRole} pending invitations`} for ${activeRoleName} will invite as Steward instead.`,
          'People who already have it keep it.',
        ]
          .filter(Boolean)
          .join(' ')}
        confirmText="Delete role"
      />
      <AddRoleModal
        open={addRoleOpen}
        onClose={() => setAddRoleOpen(false)}
        onConfirm={handleAddRoleConfirm}
      />
      {inviteOpen && canInviteToRole && activeRoleName && (
        <AddCrewModal role={activeRoleName} onClose={() => setInviteOpen(false)} />
      )}
      <AssignRoleModal
        open={assignOpen && !!activeRoleName}
        roleName={roleLabel(activeRoleName)}
        candidates={peopleOutsideRole(activeRoleName)}
        busy={personRoleChanging}
        onClose={() => setAssignOpen(false)}
        onAssign={(person) => void handleAssignToRole(person)}
      />
    </div>
  );
};
